import { useEffect, useState } from "react";
import api, { apiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import { rupee } from "@/lib/constants";
import { Loader2, Plus, X, Star, ShieldCheck, FileText, CheckCircle2, ExternalLink, Pencil, Wallet } from "lucide-react";

const VERIFY = ["Draft", "Submitted", "Under Review", "Approved", "Verified", "Rejected", "Suspended"];
const VC = {
    Verified: "bg-emerald-50 text-emerald-700", Approved: "bg-emerald-50 text-emerald-700",
    Draft: "bg-slate-100 text-slate-600", Submitted: "bg-blue-50 text-blue-700",
    "Under Review": "bg-amber-50 text-amber-700", Rejected: "bg-red-50 text-red-700", Suspended: "bg-red-50 text-red-700",
};
const CHECKS = [
    { k: "id_verified", l: "Government ID verified" },
    { k: "phone_verified", l: "Phone number verified" },
    { k: "skills_confirmed", l: "Skills / experience confirmed" },
    { k: "address_verified", l: "Address confirmed" },
];
const CARE_CHECKS = [
    { k: "id_verified", l: "Aadhaar / Government ID verified" },
    { k: "address_verified", l: "Address proof verified" },
    { k: "certificate_verified", l: "Caregiver / Nursing certificate verified (GNM/ANM/course)" },
    { k: "medical_fit", l: "Medical fitness certificate verified" },
    { k: "references_called", l: "2 references called & confirmed" },
    { k: "phone_verified", l: "Phone number verified" },
    { k: "care_orientation", l: "Elder-care conduct & safety orientation done" },
];
const checksFor = (pro) => (pro.track === "care" ? CARE_CHECKS : CHECKS);
const POLICE_STAGES = ["Not Started", "In Verification", "Verified"];
const DOC_LABEL = { photo: "Profile Photo", govt_id: "Aadhaar / Government ID", skill: "Skill Proof", police: "Police Verification", address_proof: "Address Proof", care_certificate: "Caregiver / Nursing Certificate", medical_fitness: "Medical Fitness", first_aid: "First-aid / CPR" };
const TrackBadge = ({ p }) => <span data-testid={`track-${p.pro_id}`} className={`ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${p.track === "care" ? "bg-rose-50 text-rose-600" : "bg-orange/10 text-orange-dark"}`}>{p.track === "care" ? "Caregiver" : "Services"}</span>;

export default function AdminProfessionals({ care = false }) {
    const [rows, setRows] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [created, setCreated] = useState(null);
    const [active, setActive] = useState(null);

    const load = () => api.get("/admin/professionals").then((r) => setRows(r.data.filter((p) => (p.track === "care") === care))).catch(() => setRows([]));
    useEffect(() => { load(); }, [care]); // eslint-disable-line

    const pending = rows?.filter((r) => ["Submitted", "Under Review"].includes(r.verification_status)).length || 0;

    return (
        <div>
            <div className="flex items-center justify-between">
                <div><h1 className="font-heading text-2xl font-extrabold text-navy" data-testid="pros-page-title">{care ? "Caregivers" : "Professionals"}</h1><p className="text-sm text-navy/55">{rows?.length || 0} {care ? "HOMIIE Care caregivers" : "home-service pros"} · <span className="font-semibold text-amber-600">{pending} awaiting review</span>{care ? " · nurses, doctors & labs are under Healthcare › Providers" : ""}</p></div>
                {!care && <button data-testid="add-pro-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 rounded-full bg-orange px-4 py-2.5 text-sm font-bold text-white"><Plus size={17} /> Add professional</button>}
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                <div className="overflow-x-auto homiie-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                            <tr><th className="px-4 py-3">Pro ID</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Services</th><th className="px-4 py-3">Rating</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Profile</th></tr>
                        </thead>
                        <tbody>
                            {rows?.map((p) => (
                                <tr key={p.id} className="border-t border-navy/5" data-testid={`pro-${p.pro_id}`}>
                                    <td className="px-4 py-3 font-heading font-bold text-navy">{p.pro_id}</td>
                                    <td className="px-4 py-3 text-navy/80">{p.name}<TrackBadge p={p} /><div className="text-xs text-navy/45">{p.phone}</div></td>
                                    <td className="px-4 py-3 text-navy/60">{(p.services || []).join(", ") || "—"}</td>
                                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-navy"><Star size={13} className="fill-orange text-orange" />{p.rating || "New"}</span></td>
                                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${VC[p.verification_status] || "bg-slate-100"}`}>{p.verification_status}</span></td>
                                    <td className="px-4 py-3">
                                        <button data-testid={`review-${p.pro_id}`} onClick={() => setActive(p)} className="rounded-full border border-navy/15 px-3 py-1.5 text-xs font-bold text-navy hover:border-orange hover:text-orange">Manage</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {active && <ProfileDrawer pro={active} onClose={() => setActive(null)} onSaved={() => { load(); }} onClosed={() => setActive(null)} />}
            {showNew && <NewPro onClose={() => setShowNew(false)} onDone={(c) => { setShowNew(false); setCreated(c); load(); }} />}
            {created && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={() => setCreated(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()}>
                        <h2 className="font-heading text-lg font-extrabold text-navy">Professional created</h2>
                        <div className="mt-4 rounded-xl bg-navy/5 p-4">
                            <p className="text-xs text-navy/50">Pro ID</p><p className="font-heading text-xl font-extrabold text-navy">{created.pro_id}</p>
                            <p className="mt-2 text-xs text-navy/50">Referral code</p><p className="font-heading text-xl font-extrabold text-orange">{created.referral_code}</p>
                        </div>
                        <button onClick={() => setCreated(null)} className="mt-4 w-full rounded-full bg-orange py-2.5 text-sm font-bold text-white">Done</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProfileDrawer({ pro, onClose, onSaved }) {
    const [tab, setTab] = useState("profile");
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/40" onClick={onClose} data-testid="review-drawer">
            <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6 homiie-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="mb-3 flex items-center justify-between">
                    <div><h2 className="font-heading text-xl font-extrabold text-navy">{pro.name}</h2><p className="text-sm text-navy/50">{pro.pro_id} · {pro.phone}</p></div>
                    <button onClick={onClose} data-testid="review-close"><X size={22} className="text-navy/50" /></button>
                </div>
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${VC[pro.verification_status] || "bg-slate-100"}`}>{pro.verification_status}</span>

                <div className="mt-4 flex gap-1 rounded-full bg-navy/5 p-1">
                    {[["profile", "Profile"], ["verification", "Verification"], ["payouts", "Payouts"]].map(([k, l]) => (
                        <button key={k} data-testid={`protab-${k}`} onClick={() => setTab(k)} className={`flex-1 rounded-full py-2 text-xs font-bold transition-colors ${tab === k ? "bg-white text-navy shadow-sm" : "text-navy/55"}`}>{l}</button>
                    ))}
                </div>

                {tab === "profile" && <ProfileTab pro={pro} onSaved={onSaved} />}
                {tab === "verification" && <VerificationTab pro={pro} onSaved={onSaved} />}
                {tab === "payouts" && <PayoutsTab pro={pro} />}
            </div>
        </div>
    );
}

const inp = "w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange";

function ProfileTab({ pro, onSaved }) {
    const [f, setF] = useState({
        name: pro.name || "", phone: pro.phone || "", experience: pro.experience || "",
        services: (pro.services || []).join(", "), service_areas: (pro.service_areas || []).join(", "),
        availability: pro.availability !== false,
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const save = async () => {
        if (!f.name.trim() || !f.phone.trim()) return toast.error("Name and phone required");
        setBusy(true);
        try {
            await api.patch(`/admin/professionals/${pro.id}`, {
                name: f.name.trim(), phone: f.phone.trim(), experience: f.experience,
                services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
                service_areas: f.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
                availability: f.availability,
            });
            toast.success("Profile updated"); onSaved();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="mt-4 space-y-3">
            {[["name", "Name"], ["phone", "Phone"], ["experience", "Experience (e.g. 5 years)"], ["services", "Services (electrician, ac)"], ["service_areas", "Areas (Pal, Basni)"]].map(([k, l]) => (
                <div key={k}><label className="mb-1 block text-xs font-semibold text-navy/60">{l}</label><input data-testid={`profile-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inp} /></div>
            ))}
            <div className="flex items-center justify-between rounded-xl border border-navy/15 px-3 py-3">
                <div><p className="text-sm font-bold text-navy">Accepting jobs</p><p className="text-xs text-navy/45">{f.availability ? "Available / online" : "Offline"}</p></div>
                <button data-testid="profile-availability" onClick={() => set("availability", !f.availability)} className={`relative h-6 w-11 rounded-full transition-colors ${f.availability ? "bg-emerald-500" : "bg-navy/20"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${f.availability ? "left-[22px]" : "left-0.5"}`} />
                </button>
            </div>
            <button data-testid="save-profile-btn" disabled={busy} onClick={save} className="w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}SAVE PROFILE</button>
        </div>
    );
}

function VerificationTab({ pro, onSaved }) {
    const [checklist, setChecklist] = useState(pro.checklist || {});
    const [police, setPolice] = useState(pro.police_verification || "Not Started");
    const [busy, setBusy] = useState(false);
    const docs = pro.documents || [];
    const token = getToken();

    const save = async (status) => {
        setBusy(true);
        try {
            await api.patch(`/admin/professionals/${pro.id}`, { checklist, police_verification: police, ...(status ? { verification_status: status } : {}) });
            toast.success(status ? `Application ${status}` : "Review saved"); onSaved();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const allChecked = checksFor(pro).every((c) => checklist[c.k]) && police === "Verified";

    return (
        <div className="mt-4">
            <div className="rounded-2xl bg-navy/[0.03] p-4 text-sm">
                <p className="text-navy/70"><b>Track:</b> <TrackBadge p={pro} /></p>
                <p className="mt-1 text-navy/70"><b>Skills:</b> {(pro.services || []).join(", ") || "—"}</p>
                {pro.track === "care" && <p className="mt-1 text-navy/70" data-testid="pro-references"><b>References:</b> {(pro.references || []).map((r) => `${r.name} (${r.phone}${r.relation ? ", " + r.relation : ""})`).join(" · ") || "—"}</p>}
                <p className="mt-1 text-navy/70"><b>Areas:</b> {(pro.service_areas || []).join(", ") || "—"}</p>
                <p className="mt-1 text-navy/70"><b>Experience:</b> {pro.experience || "—"}</p>
                {pro.bio && <p className="mt-1 text-navy/70"><b>About:</b> {pro.bio}</p>}
            </div>

            <h3 className="mt-5 font-heading text-sm font-bold text-navy">Documents</h3>
            <div className="mt-2 space-y-2">
                {docs.length === 0 && <p className="text-xs text-navy/50">No documents submitted.</p>}
                {docs.map((d) => (
                    <a key={d.type} href={`${d.url}?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`review-doc-${d.type}`}
                        className="flex items-center gap-2 rounded-xl border border-navy/10 p-3 text-sm font-medium text-navy hover:border-orange">
                        <FileText size={16} className="text-navy/50" /> {DOC_LABEL[d.type] || d.type} <ExternalLink size={13} className="ml-auto text-orange" />
                    </a>
                ))}
            </div>

            <h3 className="mt-5 font-heading text-sm font-bold text-navy">Verification checklist</h3>
            <div className="mt-2 space-y-2">
                {checksFor(pro).map((c) => (
                    <label key={c.k} data-testid={`check-${c.k}`} className="flex cursor-pointer items-center gap-3 rounded-xl border border-navy/10 p-3 text-sm text-navy">
                        <input type="checkbox" checked={!!checklist[c.k]} onChange={(e) => setChecklist({ ...checklist, [c.k]: e.target.checked })} className="h-4 w-4 accent-orange" />
                        {c.l}
                    </label>
                ))}
            </div>

            <h3 className="mt-5 flex items-center gap-2 font-heading text-sm font-bold text-navy"><ShieldCheck size={16} className="text-orange" /> Police verification (2-stage)</h3>
            <div className="mt-2 flex gap-2">
                {POLICE_STAGES.map((s) => (
                    <button key={s} data-testid={`police-${s.replace(/ /g, "-")}`} onClick={() => setPolice(s)}
                        className={`flex-1 rounded-xl border px-2 py-2 text-xs font-bold transition-colors ${police === s ? "border-orange bg-orange text-white" : "border-navy/15 text-navy/70"}`}>{s}</button>
                ))}
            </div>
            {!allChecked && <p className="mt-3 text-xs text-amber-600">Complete all checks and police verification to safely approve.</p>}

            <div className="mt-6 space-y-2">
                <button data-testid="approve-btn" disabled={busy} onClick={() => save("Approved")} className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3 text-sm font-bold text-white disabled:opacity-60"><CheckCircle2 size={16} /> APPROVE</button>
                <div className="flex gap-2">
                    <button data-testid="under-review-btn" disabled={busy} onClick={() => save("Under Review")} className="flex-1 rounded-full border border-navy/20 py-3 text-sm font-bold text-navy disabled:opacity-60">Under Review</button>
                    <button data-testid="reject-btn" disabled={busy} onClick={() => save("Rejected")} className="flex-1 rounded-full border border-red-200 bg-red-50 py-3 text-sm font-bold text-red-600 disabled:opacity-60">Reject</button>
                </div>
                <button data-testid="save-review-btn" disabled={busy} onClick={() => save(null)} className="w-full rounded-full py-2.5 text-xs font-bold text-navy/60 disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}Save review without changing status</button>
            </div>
        </div>
    );
}

function PayoutsTab({ pro }) {
    const [data, setData] = useState(null);
    const load = () => api.get(`/admin/professionals/${pro.id}/payouts`).then((r) => setData(r.data)).catch(() => setData({ payouts: [], total_paid: 0, total_due: 0 }));
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const mark = async (id, status) => {
        try { await api.patch(`/admin/payouts/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange" /></div>;

    return (
        <div className="mt-4">
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-amber-50 p-4"><p className="text-xs font-semibold text-amber-700/70">Total due</p><p data-testid="payout-total-due" className="font-heading text-xl font-extrabold text-amber-700">{rupee(data.total_due)}</p></div>
                <div className="rounded-2xl bg-emerald-50 p-4"><p className="text-xs font-semibold text-emerald-700/70">Total paid</p><p data-testid="payout-total-paid" className="font-heading text-xl font-extrabold text-emerald-700">{rupee(data.total_paid)}</p></div>
            </div>

            <div className="mt-4 space-y-2.5">
                {data.payouts.length === 0 && <p className="py-8 text-center text-sm text-navy/45">No payouts yet for this professional.</p>}
                {data.payouts.map((p) => (
                    <div key={p.id} data-testid={`payout-${p.booking_ref}`} className="rounded-2xl border border-navy/10 p-3.5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-heading text-sm font-bold text-navy">{p.service_name || "Service"}</p>
                                <p className="text-xs text-navy/45">{p.booking_ref}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-heading text-base font-extrabold text-navy">{rupee(p.final_payout)}</p>
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${p.status === "Paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                            </div>
                        </div>
                        <div className="mt-2.5">
                            {p.status === "Paid"
                                ? <button data-testid={`mark-due-${p.booking_ref}`} onClick={() => mark(p.id, "Due")} className="w-full rounded-full border border-navy/15 py-2 text-xs font-bold text-navy">Mark as Due</button>
                                : <button data-testid={`mark-paid-${p.booking_ref}`} onClick={() => mark(p.id, "Paid")} className="flex w-full items-center justify-center gap-1.5 rounded-full bg-navy py-2 text-xs font-bold text-white"><Wallet size={13} /> Mark Paid</button>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NewPro({ onClose, onDone }) {
    const [f, setF] = useState({ name: "", phone: "", password: "homiie123", experience: "", services: "", service_areas: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const submit = async () => {
        if (!f.name || !f.phone) return toast.error("Name and phone required");
        setBusy(true);
        try {
            const { data } = await api.post("/admin/professionals", { name: f.name, phone: f.phone, password: f.password, experience: f.experience, services: f.services.split(",").map((s) => s.trim()).filter(Boolean), service_areas: f.service_areas.split(",").map((s) => s.trim()).filter(Boolean) });
            toast.success("Professional created"); onDone(data);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-extrabold text-navy">Add professional</h2><button onClick={onClose}><X size={20} className="text-navy/50" /></button></div>
                <div className="space-y-3">
                    {[["name", "Name *"], ["phone", "Phone *"], ["password", "Password"], ["experience", "Experience (e.g. 5 years)"], ["services", "Services (electrician, ac)"], ["service_areas", "Areas (Pal, Basni)"]].map(([k, l]) => (
                        <div key={k}><label className="mb-1 block text-xs font-semibold text-navy/60">{l}</label><input data-testid={`newpro-${k}`} value={f[k]} onChange={set(k)} className="w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange" /></div>
                    ))}
                </div>
                <button data-testid="submit-new-pro" disabled={busy} onClick={submit} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} CREATE</button>
            </div>
        </div>
    );
}
